import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FmcHost } from 'fmc-sdk';
import canUseDOM from 'utils/canUseDOM';
import { selectTenant } from 'state/tenant';

const FmcHostContext = createContext<FmcHost | null>(null);

export const FmcHostProvider: React.FC = ({ children }) => {
  const tenant = useSelector(selectTenant);
  if (tenant === undefined) {
    throw new Error('tenant undefined');
  }

  const [fmcHost] = useState(() => {
    if (canUseDOM && tenant.enabledFeatures.enhancedPresentment) {
      return new FmcHost({
        targetOrigins:
          tenant.enabledFeatures.enhancedPresentment.frameAncestors,
      });
    } else {
      return null;
    }
  });
  return (
    <FmcHostContext.Provider value={fmcHost}>
      {children}
    </FmcHostContext.Provider>
  );
};

/**
 * Returns the a dispatch to send actions to the embedded iframe
 *
 * The FmcSdk instance is not available when:
 * - when FmcSdkProvider is not an ancestor of the component
 * - during server side rendering
 *
 * These circumstances should be avoided. Dispatching will be a NO-OP.
 */
const useFmcHost = () => {
  const fmcSdk = useContext(FmcHostContext);
  const dispatch: FmcHost['dispatch'] = useCallback(
    action => {
      if (fmcSdk !== null) {
        fmcSdk.dispatch(action);
      }
    },
    [fmcSdk],
  );
  return dispatch;
};

export default useFmcHost;
